import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiLock, FiLogOut } from 'react-icons/fi';
import {
  clearClientPortalToken,
  getClientPortalMe,
  getClientPortalToken,
  getClientPurchaseOrderById,
  getClientPurchaseOrderCatalog,
  submitClientPurchaseOrder,
  updateClientPurchaseOrder,
} from '../api/clientPortalApi';
import ClientPortalWorkflowNav from '../components/ClientPortalWorkflowNav';
import DocumentHeader from '../components/DocumentHeader';
import InlineLoading from '../components/InlineLoading';
import PurchaseOrderCatalogEditor from '../components/PurchaseOrderCatalogEditor';
import { notify } from '../utils/alertBus';
import { isPurchaseOrderPreparingStatus } from '../utils/purchaseOrderStatus';

const isOrderEditableByClient = (order) => (
  order
  && !order.purchaseId
  && isPurchaseOrderPreparingStatus(order.status)
);

const ClientPurchaseOrderEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [client, setClient] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const load = useCallback(async () => {
    if (!getClientPortalToken()) {
      navigate('/', { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const me = await getClientPortalMe();
      if (me?.mustChangePassword) {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      const [catalogResponse, orderResponse] = await Promise.all([
        getClientPurchaseOrderCatalog(),
        isEditMode ? getClientPurchaseOrderById(id) : Promise.resolve(null),
      ]);
      setClient(me);
      setCatalog(Array.isArray(catalogResponse?.products) ? catalogResponse.products : []);
      setOrder(orderResponse);
      if (isEditMode && !isOrderEditableByClient(orderResponse)) {
        setError('Esta orden ya no puede ser editada.');
      }
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      if (requestError?.response?.status === 401) {
        clearClientPortalToken();
        navigate('/', { replace: true });
        return;
      }
      setError(requestError?.response?.data?.message || 'No se pudo cargar la orden.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    clearClientPortalToken();
    navigate('/', { replace: true });
  };

  const handleSubmit = async ({ items, notes, resetEditor }) => {
    setSuccessMessage('');
    if (isEditMode) {
      await updateClientPurchaseOrder(id, { items, notes });
      notify('Orden de compra actualizada.');
      navigate('/client-portal/purchase-orders');
      return;
    }
    const response = await submitClientPurchaseOrder({ items, notes });
    const requestId = response?.purchaseOrder?.id;
    resetEditor();
    setSuccessMessage(`Orden de compra enviada. Numero: #${requestId || ''}`);
  };

  const topContent = useMemo(() => (
    <ClientPortalWorkflowNav
      activeKey="purchase-order"
      onNavigate={navigate}
      className="mb-4"
    />
  ), [navigate]);

  const successContent = successMessage ? (
    <div>
      <p className="text-sm font-semibold text-gray-900">Orden guardada</p>
      <p className="mt-0.5 text-sm text-emerald-700">{successMessage}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate('/client-portal/purchase-orders')}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          Ver mis ordenes
        </button>
        <button
          type="button"
          onClick={() => setSuccessMessage('')}
          className="rounded border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Crear otra
        </button>
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <InlineLoading label="Cargando orden" />
      </div>
    );
  }

  const blockedEdit = isEditMode && (!order || !isOrderEditableByClient(order));

  return (
    <div className="flex min-h-screen flex-col bg-gray-100">
      <DocumentHeader
        stickyOffsetClassName="top-0"
        title={isEditMode ? `Editar orden #${id}` : 'Crear orden de compra'}
        showCartButton={false}
        headerActions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/client-portal/change-password')}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLock />
              Contraseña
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLogOut />
              Salir
            </button>
          </div>
        }
        clientName={client?.name || ''}
        clientRut={client?.rut || 'Cliente'}
        showClientType={false}
        emptyClientLabel={client?.name || 'Cliente'}
      />

      {blockedEdit ? (
        <main className="mx-auto w-full max-w-3xl p-4">
          {topContent}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {error || 'Esta orden ya no puede ser editada.'}
            <button
              type="button"
              onClick={() => navigate('/client-portal/purchase-orders')}
              className="mt-3 block rounded bg-amber-700 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-800"
            >
              Volver a mis ordenes
            </button>
          </div>
        </main>
      ) : (
        <PurchaseOrderCatalogEditor
          catalog={catalog}
          initialItems={isEditMode ? order?.Items || [] : []}
          initialNotes={isEditMode ? order?.notes || '' : ''}
          resetKey={isEditMode ? `edit-${order?.id}-${order?.updatedAt}` : `new-${successMessage}`}
          submitLabel={isEditMode ? 'Guardar cambios' : 'Enviar orden de compra'}
          cartDescription="Los totales se muestran antes de guardar."
          notesPlaceholder="Notas para la orden (opcional)"
          onSubmit={handleSubmit}
          topContent={topContent}
          successContent={successContent}
        />
      )}
    </div>
  );
};

export default ClientPurchaseOrderEditorPage;
